import React, { useMemo, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { LoadingBlock } from '../components/LoadingBlock';
import { SectionCard } from '../components/SectionCard';

const ALERT_TYPES = [
  { value: 'match_reminder', label: 'Lembrete de jogo' },
  { value: 'schedule_update', label: 'Mudança de data/horário' },
  { value: 'championship_update', label: 'Aviso do campeonato' },
  { value: 'operational', label: 'Operacional relevante' },
];

const SCOPES = [
  { value: 'match', label: 'Jogo' },
  { value: 'team', label: 'Time' },
  { value: 'championship', label: 'Campeonato' },
  { value: 'tenant', label: 'Tenant/região' },
];

const DEFAULT_FORM = {
  alert_type: 'match_reminder',
  scope: 'match',
  scope_id: '',
  title: '',
  message: '',
};

function dateLabel(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function matchLabel(match) {
  const home = match.home_team_name || match.home_team?.name || 'Mandante';
  const away = match.away_team_name || match.away_team?.name || 'Visitante';
  const date = match.scheduled_at ? ` • ${dateLabel(match.scheduled_at)}` : '';
  return `${home} x ${away}${date}`;
}

function statusLabel(status) {
  switch (status) {
    case 'sent':
      return 'Enviado';
    case 'partial':
      return 'Parcial';
    case 'blocked':
      return 'Bloqueado';
    case 'failed':
      return 'Falhou';
    default:
      return status || '-';
  }
}

export function SmsAlertsPage({
  loading,
  error,
  data,
  championships = [],
  teams = [],
  matches = [],
  onSendAlert,
  sending,
  canManage,
}) {
  const [form, setForm] = useState(DEFAULT_FORM);

  const targetOptions = useMemo(() => {
    if (form.scope === 'championship') {
      return championships.map((item) => ({ value: item.id, label: item.name }));
    }
    if (form.scope === 'team') {
      return teams.map((item) => ({ value: item.id, label: item.name }));
    }
    if (form.scope === 'match') {
      return matches.map((item) => ({ value: item.id, label: matchLabel(item) }));
    }
    return [];
  }, [championships, form.scope, matches, teams]);

  const smsEnabled = Boolean(data?.enabled);
  const remainingToday = data?.remaining_today ?? 0;
  const preview = `Perifa Pro: ${form.title || 'Título'} - ${form.message || 'Mensagem curta do alerta'}`;
  const canSubmit = canManage && smsEnabled && !sending && (form.scope === 'tenant' || form.scope_id) && form.title.trim() && form.message.trim();

  if (loading) return <LoadingBlock label="Carregando alertas SMS..." />;
  if (error) return <div className="page-error">{error}</div>;

  return (
    <div className="page-grid two-columns">
      <SectionCard
        title="Alertas SMS"
        subtitle="Dispare somente avisos úteis, curtos e vinculados ao tenant, jogo, time ou campeonato correto."
      >
        <div className="callout-grid">
          <div className="callout-box compact-box">
            <span className="eyebrow">Status</span>
            <strong>{smsEnabled ? 'SMS configurado' : 'SMS desativado'}</strong>
            <p>{smsEnabled ? `Restam ${remainingToday} envios no limite diário.` : 'Ative SMS_ENABLED e configure o provedor antes de enviar.'}</p>
          </div>
          <div className="callout-box compact-box">
            <span className="eyebrow">Regra</span>
            <strong>Consentimento obrigatório</strong>
            <p>O backend só envia para usuários ativos com telefone e aceite de SMS.</p>
          </div>
        </div>

        <form
          className="admin-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) return;
            onSendAlert({
              ...form,
              scope_id: form.scope === 'tenant' ? null : Number(form.scope_id),
            }).then?.(() => setForm(DEFAULT_FORM));
          }}
        >
          <div className="form-row">
            <label>
              Tipo de alerta
              <select value={form.alert_type} onChange={(event) => setForm({ ...form, alert_type: event.target.value })}>
                {ALERT_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Destino
              <select value={form.scope} onChange={(event) => setForm({ ...form, scope: event.target.value, scope_id: '' })}>
                {SCOPES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>

          {form.scope !== 'tenant' ? (
            <label>
              Selecionar {SCOPES.find((item) => item.value === form.scope)?.label.toLowerCase()}
              <select value={form.scope_id} onChange={(event) => setForm({ ...form, scope_id: event.target.value })} required>
                <option value="">Escolha um item</option>
                {targetOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          ) : null}

          <div className="form-row">
            <label>
              Título curto
              <input maxLength={120} value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="Jogo começa em breve" required />
            </label>
            <label>
              Mensagem
              <input maxLength={240} value={form.message} onChange={(event) => setForm({ ...form, message: event.target.value })} placeholder="Favela United x Quebrada FC hoje às 15h." required />
            </label>
          </div>

          <div className="callout-box compact-box">
            <span className="eyebrow">Prévia</span>
            <p>{preview.length > 300 ? `${preview.slice(0, 297)}...` : preview}</p>
          </div>

          {!canManage ? <div className="page-error small">Seu perfil não pode disparar SMS administrativo.</div> : null}
          <button className="primary-button" type="submit" disabled={!canSubmit}>
            {sending ? 'Enviando...' : 'Enviar alerta SMS'}
          </button>
        </form>
      </SectionCard>

      <SectionCard title="Histórico" subtitle="Registro de disparos, sem expor telefone completo.">
        <DataTable
          rows={data?.items || []}
          emptyText="Nenhum alerta SMS enviado ainda."
          columns={[
            { key: 'created_at', label: 'Quando', render: (row) => dateLabel(row.created_at) },
            { key: 'target_label', label: 'Destino' },
            { key: 'title', label: 'Título' },
            { key: 'recipient_count', label: 'Público' },
            { key: 'sent_count', label: 'Enviados' },
            { key: 'status', label: 'Status', render: (row) => statusLabel(row.status) },
          ]}
        />
      </SectionCard>
    </div>
  );
}
